import {Search} from 'lucide-react';
import {PhoneHQSortHeader, PhoneHQInput, PhoneHQLoader, PhoneHQIconAvatar, StatusBadge, PhoneHQGlyph, PhoneHQTooltip} from 'ch3-ui-lib';
import {Msisdn, RecordingMode} from "../../store/features/Msisdns/msisdnService";
import {useSelector} from "react-redux";
import {getFlag, getFormatedPhoneNumber} from "../../utilis/msisdnUtilis";
import {FlagImage} from "react-international-phone";
import {Link, useLocation} from "react-router-dom";
import {useEffect, useMemo, useState} from "react";
import {debounce} from "lodash";
import noGroupsIcon from "../../assets/empty.svg";
import useMediaQuery from "../../utilis/screenWidthUtils.ts";
import {
    useFetchMsisdnSettingsInfoQuery,
    useFetchMsisdnsAdminQuery,
    useFetchMsisdnsListQuery,
} from "../../store/features/MsisdnSettings/msisdnSettingsService";
import {selectUserId} from "../../store/features/Auth/authSlice";
import AdminPanelRestrictedView from "../../components/AdminPanelRestrictedView/AdminPanelRestrictedView";
import {RootState} from "../../store/store.ts";

interface PhoneNumbersProps {
    admin?: boolean;
}

interface PhoneNumberRowProps {
    msisdn: Msisdn;
    admin: boolean;
}

const isRestrictedRecordingMode = (recordingMode?: string | null): boolean =>
    recordingMode?.toUpperCase() === RecordingMode.RESTRICTED;

const PhoneNumberRow = ({msisdn, admin}: PhoneNumberRowProps) => {
    const ensReserved = msisdn.reservedFor === 'ENS';
    const shouldFetchSettingsInfo = admin && !msisdn.recordingMode && !ensReserved;
    const {data: msisdnSettingsInfo} = useFetchMsisdnSettingsInfoQuery(msisdn.msisdnId.toString(), {
        skip: !shouldFetchSettingsInfo,
    });
    const recordingMode = msisdn.recordingMode ?? msisdnSettingsInfo?.recordingMode;
    const isRecordingRestricted = isRestrictedRecordingMode(recordingMode);
    const rowContent = (
        <div className='flex justify-between'>
            <div className={'flex gap-3'}>
                <div className='text-2xl items-end flex'>
                    <PhoneHQIconAvatar icon={msisdn.icon ? msisdn.icon : 'home'} color={msisdn.iconColor? msisdn.iconColor : 'blue'} size="32px"/>
                </div>
                <div>
                    <div className='text-sm flex items-center gap-1'>
                        {msisdn.label ?? 'No name'}
                        {ensReserved &&
                            <span className='rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800'>
                                ENS reserved
                            </span>
                        }
                        {isRecordingRestricted &&
                            <span data-tooltip='Recording managed by administrator' className='inline-flex'>
                                <PhoneHQGlyph name='lock' size={14} className='text-ink-muted'/>
                            </span>
                        }
                    </div>
                    <PhoneHQTooltip content={'+' + msisdn.msisdn} align='start'><div className='flex items-center text-xs'>

                            <FlagImage iso2={getFlag(msisdn.msisdn)} size={16} />
                            <div className={'ml-2'}>
                                {getFormatedPhoneNumber(msisdn.msisdn, true)}
                            </div>
                        </div></PhoneHQTooltip>
                </div>
            </div>
            <div className='mr-4' >
                {msisdn?.assigmentScope === 'PRIVATE' && <StatusBadge tone="neutral">Private</StatusBadge>}
            </div>
        </div>
    );

    return (
        <tr>
            <td className={`w-full border-0 border-t border-solid border-surface-muted px-4 py-2 hover:bg-surface-muted ${ensReserved ? '' : 'cursor-pointer'}`}>
                {ensReserved ? rowContent : (
                    <Link to={ (admin ? '/admin' : '' ) +  `/phoneNumbers/${msisdn.msisdnId}`}>
                        {rowContent}
                    </Link>
                )}
            </td>
        </tr>
    );
};

export const PhoneNumbers = ({admin = false}: PhoneNumbersProps) => {
    const userId = useSelector(selectUserId);
    const [phrase, setPhrase] = useState<string>('');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const location = useLocation();
    const isAdminPath = location.pathname.includes('admin');
    const isMobile = useMediaQuery('(max-width: 960px)');
    const role = useSelector((state: RootState) => state.auth.role);

    const adminMsisdnsQuery = useFetchMsisdnsAdminQuery({ phrase }, { skip: !admin });
    const userMsisdnsQuery = useFetchMsisdnsListQuery({ phrase, userId }, { skip: admin });
    const msisdns = admin ? adminMsisdnsQuery.data : userMsisdnsQuery.data;
    const isLoading = admin ? adminMsisdnsQuery.isLoading : userMsisdnsQuery.isLoading;

    const handlePhraseChange = useMemo(
        () => debounce((newPhrase: string) => {
            setPhrase(newPhrase);
        }, 1000),
        []
    );

    useEffect(() => () => {
        handlePhraseChange.cancel();
    }, [handlePhraseChange]);

    const handleSort = () => {
        setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    };

    const sortedMsisdns = useMemo(() => {
        if (!msisdns?.data) return [];
        return [...msisdns.data].sort((a, b) => {
            const labelA = a.label?.toLowerCase() || '';
            const labelB = b.label?.toLowerCase() || '';
            return sortOrder === 'asc' ? labelA.localeCompare(labelB) : labelB.localeCompare(labelA);
        });
    }, [msisdns, sortOrder]);

    if (isLoading) {
        return (
            <div className={`flex w-full `}>
                <PhoneHQLoader/>
            </div>
        );
    }
    return(
        <>
        {Boolean(role === 'USER') &&  isAdminPath &&
            <AdminPanelRestrictedView></AdminPanelRestrictedView>
        }
        {Boolean(msisdns) &&
        <div className='w-full'>
          
            <div className={`${isMobile ? ' px-4' : ' p-8'}`}>
                <div className='mb-3 flex items-center justify-between gap-3'>
                    <span className='text-footnote font-medium text-ink-muted'>{sortedMsisdns.length} {sortedMsisdns.length === 1 ? 'line' : 'lines'}</span>
                    <PhoneHQInput  phq-id='phoneNumbers-searchInput'  placeholder={'Search'} containerClassName={`${isMobile ? ' ' : ' min-w-[240px] max-w-[240px]'}`} prefixIcon={<Search size={16} aria-hidden="true" className="!p-0"/>} onChange={({target: {value}}) => handlePhraseChange(value)} />
                </div>
                <div className={` overflow-y-auto`}>
                    <div className='overflow-hidden rounded-card bg-surface'>
                        <table className='w-full border-collapse'>
                            <thead>
                            <tr className='h-10 border-0 border-b border-solid border-line bg-canvas text-left text-caption text-ink-muted-strong'>
                                <th className='w-full cursor-pointer px-4' onClick={handleSort}>
                                    <PhoneHQSortHeader label={`Line`}/>
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            {sortedMsisdns.map((msisdn) => (
                                <PhoneNumberRow key={msisdn.msisdnId} msisdn={msisdn} admin={admin}/>
                            ))}
                            {sortedMsisdns.length === 0 && phrase &&
                                <div className='flex text-center justify-center'>
                                    <div className='w-[450px] m-14'>
                                        <div className='flex justify-center w-full'>
                                            <img className='m-4' src={noGroupsIcon} alt="No results"/>
                                        </div>
                                        <h1 className='font-bold'>
                                            No phone numbers found
                                        </h1>
                                        <div className='mb-6 mt-2'>
                                            No phone numbers found matching your search. Please double-check your criteria.
                                        </div>
                                    </div>
                                </div>
                            }
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        
        </div>
        }
        </>
    );
}
